import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@urc-ui/store/api/base';
import { HttpMethod, Tag } from '@urc-ui/store/api/types';
import { getAcceptLanguageHeader, resolveAdvertisingApiBaseUrl } from '@urc-ui/store/api/ross/utils';
import { exponentialBackoffWithJitter, getAmcAuthorizationHeaders } from '@flexible-reports/store/api/base';
import { AmcTemplateOption, AmcTemplateParameter, AmcTemplateResponse } from '@flexible-reports/store/api/templates/api-types';

const transformAmcTemplateResponse = (response: any): AmcTemplateResponse => {
  const cell = response.cells?.find(
    (cell: any) =>
      cell.cell_type === 'code' && cell.metadata?.query_type === 'INSIGHT' && cell.metadata?.notebook_types?.includes('EXPRESS')
  );

  if (!cell) {
    return {
      templateId: response.metadata?.id,
      description: response.metadata?.description,
      templateParameters: [],
      templateOptions: []
    };
  }

  const templateParameters: AmcTemplateParameter[] =
    cell.metadata.cell_parameters?.map((param: any) => {
      const shouldUseCampaignDimension =
        // "Overlap by campaign groups" template require this special logic to use campaign picker
        (response.metadata?.id === '38a7ea0537f8205f55d5c5e0335fbf98801de5265a23f2359ab23060f9230bb7' &&
          param.data_type?.type === 'ARRAY') ||
        param.name === 'campaign_id';

      return {
        name: param.name,
        inputType: shouldUseCampaignDimension ? 'CAMPAIGN_DIMENSION' : param.input_type,
        displayName: param.display_name,
        optional: param.optional,
        helpText: param.help_text,
        dataType: typeof param.data_type === 'object' && param.data_type?.type === 'ARRAY' ? 'STRING_ARRAY' : param.data_type || 'STRING'
      };
    }) || [];

  const templateOptions: AmcTemplateOption[] =
    cell.metadata.template_options?.map((option: any) => ({
      label: option.label,
      id: option.id,
      helpText: option.help_text,
      inputType: option.input_type,
      values: option.values?.map((value: any) => ({
        label: value.label,
        value: value.value,
        helpText: value.help_text
      }))
    })) || [];

  return {
    templateId: response.metadata?.id,
    description: response.metadata?.description,
    templateParameters,
    templateOptions
  };
};

export const amcTemplatesApi = createApi({
  reducerPath: 'amcTemplatesApi',
  baseQuery: retry(
    axiosBaseQuery({
      baseUrl: resolveAdvertisingApiBaseUrl()
    }),
    exponentialBackoffWithJitter({ baseDelay: 50, maxDelay: 5000, maxRetries: 5 })
  ),
  tagTypes: [Tag.AMC_TEMPLATES],
  endpoints: builder => ({
    getAmcTemplateById: builder.query({
      query: (queryId: string) => {
        return {
          url: `/amc/queries/${queryId}/notebook`,
          method: HttpMethod.GET,
          headers: { ...getAmcAuthorizationHeaders(), ...getAcceptLanguageHeader() }
        };
      },
      transformResponse: transformAmcTemplateResponse
    })
  })
});

export const { useGetAmcTemplateByIdQuery } = amcTemplatesApi;
